import { useQuery } from '@tanstack/react-query';

import { toRegionalPath } from '@/apiClients/regionalFetch';
import { maybe } from '@/async-result/utils';
import { ApiRegion } from '@/config';
import { useAuth } from '@/context/appContext';
import { useRegionalApis } from '@/context/RegionScope';
import { useQueryNeverRefetch } from '@/utils';
import { createDisposingObjectUrl, SymbolDispose } from '@/utils/queryClientDisposeOnCacheEvict';

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * The Vite dev server proxies `/__mnt` → `VITE_API_URL`. MNT only sends photo CORS headers to
 * deployed hosts (FOOD-1301), so local development against a deployed API (`bun run start:stg`)
 * fetches Canada photos same-origin through it. A local API (local MNT, or the e2e stubs on
 * localhost:1480) already allows localhost, and the e2e specs stub photos on that host, so it is
 * left alone. Vitest runs in `test` mode and deployed builds in `production`, so neither rewrites.
 */
export const resolveLocalApiUrl = (url: string, apiUrl: string): string => {
  if (
    import.meta.env.MODE !== 'development'
    || LOCAL_HOSTS.includes(new URL(apiUrl).hostname)
    || !url.startsWith(`${apiUrl}/`)
  ) {
    return url;
  }
  return `${window.location.origin}/__mnt${url.slice(apiUrl.length)}`;
};

export type ImgProps = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export const useAuthenticatedBlobQuery = (src: string | null | undefined) => {
  const { authInfo } = useAuth();
  const apis = useRegionalApis();
  const isEnabled = !!src && !!authInfo?.access_token;

  const query = useQuery({
    queryKey: ['authenticated-blob', src, authInfo?.access_token],
    queryFn: async () => {
      if (!src || !authInfo?.access_token) {
        return null;
      }
      const response =
        apis.region === ApiRegion.CA
          ? await fetch(resolveLocalApiUrl(src, apis.apiUrl), {
              headers: {
                'Authorization': `Bearer ${authInfo?.access_token}`,
              },
            })
          : await apis.fetch(toRegionalPath(apis.apiUrl, src));
      if (response.status !== 200) {
        const [text, textErr] = await maybe(response.text());
        throw new Error(`HTTP status ${response.status}: ${text || textErr || 'Unknown error'}`);
      }
      const data = await response.blob();
      return createDisposingObjectUrl(data);
    },
    ...useQueryNeverRefetch,
    staleTime: 15 * 60 * 1000,
    enabled: isEnabled,
  });

  return {
    src: query.data?.url,
    isEnabled,
    isLoading: query.isPending,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    query,
  };
};

export const ImageFromQuery = (
  props: Omit<ImgProps, 'src'> & { img: ReturnType<typeof useAuthenticatedBlobQuery> },
) => {
  const { img, ...imgProps } = props;

  if (img.isError) {
    return (
      <div className="flex items-center justify-center bg-canvas p-2 text-xs text-ink-muted" style={imgProps.style}>
        Image unavailable
      </div>
    );
  }

  if (img.isLoading) {
    return (
      <div className="flex items-center justify-center bg-canvas p-2 text-xs text-ink-muted" style={imgProps.style}>
        Loading…
      </div>
    );
  }

  if (!img.src) {
    return null;
  }

  return <img {...imgProps} alt={imgProps.alt ?? ''} src={img.src} />;
};

export const AuthenticatedImage = (props: Omit<ImgProps, 'src'> & { src: string | null }) => {
  const { src, ...imgProps } = props;
  const img = useAuthenticatedBlobQuery(src);
  return <ImageFromQuery img={img} {...imgProps} />;
};
